import { Link } from 'react-router-dom';

export function TermsPage() {
  return (
    <>
      <style>{`
        .legal-page h2 { margin-top: 2rem; margin-bottom: 0.5rem; font-size: 1.25rem; }
        .legal-page h3 { margin-top: 1.5rem; margin-bottom: 0.5rem; font-size: 1.1rem; }
        .legal-page p, .legal-page ul { margin-bottom: 1rem; }
        .legal-page ul { padding-left: 1.5rem; }
        .legal-page li { margin-bottom: 0.5rem; }
        .legal-page .legal-date { color: var(--text-muted); font-size: 0.9rem; margin-bottom: 2rem; }
      `}</style>
      <div className="container legal-page">
        <div style={{ marginBottom: '2rem' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', color: 'var(--text-color)' }}>
            <img src="/img/logo.svg" alt="SimplyTelehealth" style={{ width: '36px', height: '36px' }} />
            <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>SimplyTelehealth</span>
          </Link>
        </div>

        <h1>Terms and Conditions</h1>
        <p className="legal-date">Effective Date: March 26, 2026</p>

        <p>These Terms and Conditions ("Terms") govern your use of the SimplyTelehealth platform ("Service") operated by SimplyTelehealth ("we," "us," or "our"). By accessing or using the Service, you agree to be bound by these Terms.</p>

        <h2>1. Service Description</h2>
        <p>SimplyTelehealth provides a telehealth platform that enables healthcare providers ("Providers") to conduct video visits and SMS text-based visits with their patients ("Patients"). The Service includes:</p>
        <ul>
          <li>Video visit hosting with custom branding</li>
          <li>Phone dial-in capability for patients</li>
          <li>SMS text-based asynchronous visits</li>
          <li>Appointment scheduling and visit reminders (email and SMS)</li>
          <li>Practice management tools (branding, team management, billing)</li>
        </ul>

        <h2>2. Eligibility</h2>
        <p>The Service is intended for licensed healthcare providers and their staff. By creating a Provider account, you represent that you are authorized to provide telehealth services in your jurisdiction.</p>

        <h2>3. Provider Accounts</h2>
        <ul>
          <li>You are responsible for maintaining the confidentiality of your account credentials</li>
          <li>You are responsible for all activity that occurs under your account</li>
          <li>You must provide accurate and complete information when creating your account</li>
          <li>You must promptly update your information if it changes</li>
        </ul>

        <h2>4. Patient Access</h2>
        <p>Patients access the Service through visit links and do not create accounts. Patients may provide their name, email, and phone number to participate in visits. By using the Service, Patients agree to these Terms.</p>

        <h2>5. SMS Text Messaging</h2>

        <h3>5.1 Consent</h3>
        <p>SMS messages are only sent to individuals who have provided consent:</p>
        <ul>
          <li><strong>Visit reminders:</strong> Require explicit opt-in via checkbox before any messages are sent</li>
          <li><strong>SMS visits:</strong> The Provider initiates the session; the Patient's first reply constitutes consent to the two-way text conversation</li>
        </ul>

        <h3>5.2 Message Types and Frequency</h3>
        <ul>
          <li>Visit reminders: Up to 2 messages per scheduled appointment (24-hour and 1-hour reminders)</li>
          <li>SMS visits: Message frequency varies based on the clinical conversation. Sessions expire after 24 hours.</li>
        </ul>

        <h3>5.3 Opt-Out</h3>
        <p>You can opt out of SMS messages at any time by replying <strong>STOP</strong> to any message from SimplyTelehealth. Reply <strong>HELP</strong> for assistance. After opting out, you will receive a confirmation message and no further texts will be sent.</p>

        <h3>5.4 Costs</h3>
        <p>Message and data rates may apply depending on your mobile carrier and plan. SimplyTelehealth does not charge Patients for receiving SMS messages, but standard carrier rates apply.</p>

        <h3>5.5 Privacy of Phone Numbers</h3>
        <p>During SMS visits, messages are relayed through a SimplyTelehealth proxy number. Provider and Patient personal phone numbers are never disclosed to each other.</p>

        <h2>6. Billing and Subscriptions</h2>
        <ul>
          <li>The Service is offered on a subscription basis with a 7-day free trial</li>
          <li>Subscription fees are billed monthly via Stripe</li>
          <li>SMS visit functionality requires an active paid subscription (not available during trial)</li>
          <li>Per-practice SMS phone numbers are billed at $1.15/month each, provisioned on demand</li>
          <li>You may cancel your subscription at any time through the billing portal</li>
        </ul>

        <h2>7. HIPAA and Compliance</h2>
        <ul>
          <li>SimplyTelehealth is designed to support HIPAA compliance and maintains BAAs with its infrastructure providers</li>
          <li>Providers are responsible for their own HIPAA compliance obligations</li>
          <li>Providers should not send sensitive PHI (diagnoses, test results, Social Security numbers) via SMS text messages</li>
          <li>SMS is inherently not end-to-end encrypted; Providers should inform Patients of this limitation</li>
        </ul>

        <h2>8. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any unlawful purpose</li>
          <li>Send unsolicited or spam messages through the Service</li>
          <li>Attempt to access other users' accounts or data</li>
          <li>Reverse engineer, decompile, or disassemble the Service</li>
          <li>Use the Service to transmit malicious code or content</li>
          <li>Resell or redistribute the Service without authorization</li>
        </ul>

        <h2>9. Intellectual Property</h2>
        <p>The Service, including its design, code, and branding, is owned by SimplyTelehealth. Providers retain ownership of their practice branding (logos, names) uploaded to the platform.</p>

        <h2>10. Limitation of Liability</h2>
        <p>SimplyTelehealth provides a communication platform and is not a healthcare provider. We are not responsible for the quality or outcome of medical care delivered through the Service. The Service is provided "as is" without warranties of any kind.</p>
        <p>To the maximum extent permitted by law, SimplyTelehealth shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.</p>

        <h2>11. Termination</h2>
        <p>We may suspend or terminate your account if you violate these Terms. You may close your account at any time by contacting support. Upon termination, your access to the Service will cease and your data will be handled in accordance with our <Link to="/privacy">Privacy Policy</Link>.</p>

        <h2>12. Changes to These Terms</h2>
        <p>We may update these Terms from time to time. We will notify registered Providers of material changes via email. Continued use of the Service after changes constitutes acceptance of the revised Terms.</p>

        <h2>13. Governing Law</h2>
        <p>These Terms are governed by the laws of the State of Delaware, without regard to conflict of law principles.</p>

        <h2>14. Contact Us</h2>
        <p>If you have questions about these Terms, contact us at:</p>
        <p>
          SimplyTelehealth<br />
          Email: <a href="mailto:support@simplytelehealth.md">support@simplytelehealth.md</a><br />
          Website: <a href="https://simplytelehealth.md">simplytelehealth.md</a>
        </p>
      </div>
    </>
  );
}
